import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { getAllWithoutPagination } from 'src/app/utils/graphql';
import {
  FeaturedClusterFragment,
  FeaturedClustersGQL,
  EditFeaturedClustersGQL,
  GetClusterIfExistsGQL,
} from '@/core/graphql/graphql';

@Injectable({
  providedIn: 'root',
})
export class FeaturedClustersService {
  private readonly _loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly _loadingObservable$: Observable<boolean> = this._loadingSubject.asObservable();

  private readonly _clustersSubject: BehaviorSubject<FeaturedClusterFragment[]> = new BehaviorSubject<FeaturedClusterFragment[]>([]);
  private readonly _clusters$: Observable<FeaturedClusterFragment[]> = this._clustersSubject.asObservable();

  private _clustersWereFetched = false;

  public constructor(
    private readonly _featuredClustersGql: FeaturedClustersGQL,
    private readonly _editFeaturedClustersGql: EditFeaturedClustersGQL,
    private readonly _getClusterIfExistsGql: GetClusterIfExistsGQL,
  ) { }

  public getFeaturedClusters(): Observable<FeaturedClusterFragment[]> {
    if (this._clustersWereFetched) {
      return this._clusters$;
    }

    return from(this.fetchClusters()).pipe(
      tap((clusters) => {
        this._clustersSubject.next(clusters);
        this._clustersWereFetched = true;
      }),
      switchMap(() => this._clusters$),
    );
  }

  public updateFeaturedIds(values: number[]): void {
    this._loadingSubject.next(true);
    this._editFeaturedClustersGql.mutate({
      input: { clusterIds: values },
    }).toPromise().then(clusters => {
      this._loadingSubject.next(false);
      this._clustersSubject.next((clusters as any)?.data?.cluster?.editFeatured?.items ?? []);
    });
  }

  public addFeaturedCluster(id: number): void {
    const ids: number[] = this._clustersSubject.value.map(cluster => cluster.id);
    if (!ids.includes(id)) {
      ids.unshift(id);
      this.updateFeaturedIds(ids);
    }
  }

  public removeFeaturedCluster(id: number): void {
    const ids: number[] = this._clustersSubject.value.map(cluster => cluster.id);
    const index = ids.findIndex(next => next === id);
    ids.splice(index, 1);
    this.updateFeaturedIds(ids);
  }

  private async fetchClusters(): Promise<FeaturedClusterFragment[]> {
    this._loadingSubject.next(true);

    const result = await getAllWithoutPagination(
      (x) => this._featuredClustersGql
        .fetch({ meta: { pageCursor: x, pageSize: 199 } })
        .toPromise(), (res) => res.data.clusters,
    );
    this._loadingSubject.next(false);
    return result;
  }

  public getClusterIfExists(id: number): Observable<FeaturedClusterFragment | null> {
    this._loadingSubject.next(true);
    return this._getClusterIfExistsGql
      .fetch({ id })
      .pipe(
        map((x) => {
          this._loadingSubject.next(false);
          return x.data.cluster;
        }),
        catchError(err => {
          this._loadingSubject.next(false);
          return of(null);
        }),
      );
  }
}
